import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Zap, ArrowUpRight } from 'lucide-react';
import { useContactForm } from '../context/ContactFormContext';

interface Course {
  id: number;
  name: string;
  price: number;
  level: string;
  duration: string;
  practice: string;
  description: string;
}

const Courses = () => {
  const { t } = useTranslation();
  const { openBuy } = useContactForm();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const coursesData = t('courses.items', { returnObjects: true }) as Course[];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const cardWidth = 400;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(newIndex);
    }
  };

  const getLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      beginner: '#22c55e',
      intermediate: '#3b82f6',
      advanced: '#a855f7',
      professional: '#f59e0b',
      elite: '#ec4899',
      master: '#dc2626',
      guru: '#dc2626',
      legend: '#dc2626'
    };
    return colors[level] || '#dc2626';
  };

  return (
    <section id="courses" className="relative py-24 overflow-hidden bg-crypto-black">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-crypto-red/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-crypto-purple/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10">
        {/* Section Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-crypto-red/10 border border-crypto-red/30 text-crypto-red text-xs font-semibold tracking-wider uppercase mb-4">
                {t('courses.badge')}
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {t('courses.headingChoose')}<br />
                <span className="text-crypto-red">{t('courses.headingPath')}</span>
              </h2>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => scroll('left')}
                className="w-12 h-12 rounded-full bg-crypto-card border border-crypto-red/20 flex items-center justify-center text-crypto-red hover:bg-crypto-red hover:text-white transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-12 h-12 rounded-full bg-crypto-card border border-crypto-red/20 flex items-center justify-center text-crypto-red hover:bg-crypto-red hover:text-white transition-all duration-300"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Horizontal Scroll Container */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-8 px-4 sm:px-6 lg:px-8 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {coursesData.map((course, index) => {
            const color = getLevelColor(course.level);
            const isActive = index === activeIndex;
            
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex-shrink-0 w-[340px] md:w-[380px] snap-center"
              >
                <div 
                  className="relative h-full bg-crypto-card rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-crypto-red/20"
                  style={{
                    border: `1px solid ${isActive ? color : 'rgba(220,38,38,0.1)'}`,
                  }}
                >
                  {/* Top Color Bar */}
                  <div 
                    className="h-1.5 w-full"
                    style={{ backgroundColor: color }}
                  />

                  <div className="p-8">
                    {/* Number Badge */}
                    <div className="flex items-center justify-between mb-6">
                      <span 
                        className="text-6xl font-bold opacity-10"
                        style={{ color }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
                        style={{ 
                          backgroundColor: `${color}20`,
                          color: color
                        }}
                      >
                        {t(`courses.levels.${course.level}`)}
                      </span>
                    </div>

                    {/* Course Info */}
                    <h3 className="text-2xl font-bold text-white mb-3 leading-tight">
                      {course.name}
                    </h3>
                    
                    <p className="text-gray-400 text-sm mb-6 line-clamp-2">
                      {course.description}
                    </p>

                    {/* Stats Row */}
                    <div className="flex items-center gap-6 mb-6 text-sm">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Clock className="w-4 h-4" style={{ color }} />
                        <span>{course.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Zap className="w-4 h-4" style={{ color }} />
                        <span>{course.practice}</span>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-white">${course.price}</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <Link
                        to={`/course/${course.id}`}
                        className="flex items-center justify-between w-full px-6 py-4 bg-crypto-dark rounded-xl text-white font-medium hover:bg-crypto-darker transition-colors group"
                      >
                        <span>{t('courses.cta')}</span>
                        <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </Link>
                      
                      <button
                        onClick={() => openBuy(course.name)}
                        className="w-full py-4 rounded-xl text-white font-bold transition-all hover:shadow-lg"
                        style={{ 
                          background: `linear-gradient(135deg, ${color}, #991b1b)`
                        }}
                      >
                        {t('courses.buy')}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {coursesData.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollTo({
                    left: index * 400,
                    behavior: 'smooth'
                  });
                }
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === activeIndex 
                  ? 'w-8 bg-crypto-red' 
                  : 'w-2 bg-crypto-red/30 hover:bg-crypto-red/50'
              }`}
            />
          ))}
        </div>

        {/* Progress Info */}
        <div className="text-center mt-4 text-gray-500 text-sm">
          {t('courses.count', { current: activeIndex + 1, total: coursesData.length })}
        </div>
      </div>

    </section>
  );
};

export default Courses;
